import * as React from "react";
import { cn } from "../../lib/utils";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";
import { Radio, type RadioProps } from "./Radio";

export interface RadioOption {
  /**
   * The value of the radio option
   */
  value: string;
  /**
   * The label text for the radio option
   */
  label: string;
  /**
   * Whether this option is disabled
   */
  isDisabled?: boolean;
  /**
   * Custom class name for the option container
   */
  containerClassName?: string;
  /**
   * Custom class name for the option label
   */
  labelClassName?: string;
}

export interface RadioGroupProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "onChange" | "defaultValue"
  > {
  /**
   * Selected value (controlled)
   */
  value?: string;
  /**
   * Default selected value (uncontrolled)
   */
  defaultValue?: string;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;
  /**
   * Array of radio options
   */
  options?: RadioOption[];
  /**
   * Custom radio buttons as children (alternative to options)
   */
  children?: React.ReactNode;
  /**
   * Label for the radio group
   */
  label?: string;
  /**
   * Helper text below the radio group
   */
  helperText?: string;
  /**
   * Error text (overrides helperText when present)
   */
  errorText?: string;
  /**
   * Success text (overrides helperText when present)
   */
  successText?: string;
  /**
   * Validation state for the radio group
   */
  validationState?: "none" | "error";
  /**
   * Whether the radio group is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the radio group is required
   */
  isRequired?: boolean;
  /**
   * Whether the radio group is optional
   */
  isOptional?: boolean;
  /**
   * Size of the radio buttons
   */
  size?: "small" | "medium" | "large";
  /**
   * Name attribute for the radio inputs (auto-generated if not provided)
   */
  name?: string;
  /**
   * Orientation of the radio group
   */
  orientation?: "horizontal" | "vertical";
  /**
   * Spacing between radio options
   */
  spacing?: "tight" | "normal" | "loose";
  /**
   * Custom class for the container
   */
  containerClassName?: string;
  /**
   * Custom class for the label
   */
  labelClassName?: string;
  /**
   * Custom class for the radio group wrapper
   */
  groupClassName?: string;
  /**
   * Info heading for tooltip
   */
  infoHeading?: string;
  /**
   * Info description for tooltip
   */
  infoDescription?: string;
  /**
   * Custom Link component to render on the right side
   */
  LinkComponent?: React.ReactNode;
  /**
   * Link text
   */
  linkText?: string;
  /**
   * Link href
   */
  linkHref?: string;
  /**
   * Link click handler
   */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value: controlledValue,
      defaultValue,
      onChange,
      options,
      children,
      label,
      helperText,
      errorText,
      successText,
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      size = "medium",
      name: nameProp,
      orientation = "vertical",
      spacing = "normal",
      containerClassName,
      labelClassName,
      groupClassName,
      infoHeading,
      infoDescription,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      className,
      id,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<
      string | undefined
    >(defaultValue);
    const groupId = React.useId();
    const name = nameProp || `radio-group-${groupId}`;
    const groupRef = React.useRef<HTMLDivElement>(null);
    const radioRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const value =
      controlledValue !== undefined ? controlledValue : uncontrolledValue;

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText;
    const currentValidationState = errorText ? "error" : validationState;

    const handleChange = (optionValue: string) => {
      if (onChange) {
        onChange(optionValue);
      } else {
        setUncontrolledValue(optionValue);
      }
    };

    // Keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled) return;

      const currentIndex = radioRefs.current.findIndex(
        (ref) => ref === document.activeElement
      );

      if (currentIndex === -1) return;

      const direction =
        orientation === "horizontal"
          ? e.key === "ArrowRight"
            ? 1
            : e.key === "ArrowLeft"
            ? -1
            : 0
          : e.key === "ArrowDown"
          ? 1
          : e.key === "ArrowUp"
          ? -1
          : 0;

      if (direction === 0) return;

      e.preventDefault();

      // Find next enabled radio
      let nextIndex = currentIndex;
      const total = radioRefs.current.length;
      let attempts = 0;

      while (attempts < total) {
        nextIndex = (nextIndex + direction + total) % total;
        const nextRadio = radioRefs.current[nextIndex];

        if (nextRadio && !nextRadio.disabled) {
          nextRadio.focus();
          nextRadio.click();
          return;
        }

        attempts++;
      }
    };

    // Spacing configuration
    const spacingConfig = {
      tight: orientation === "horizontal" ? "gap-3" : "gap-2",
      normal: orientation === "horizontal" ? "gap-4" : "gap-3",
      loose: orientation === "horizontal" ? "gap-6" : "gap-4",
    };

    // Size configuration
    const sizeConfig = {
      small: {
        gap: "gap-2",
      },
      medium: {
        gap: "gap-2",
      },
      large: {
        gap: "gap-3",
      },
    };

    // Render options from array
    const renderOptions = () => {
      if (!options) return null;

      return options.map((option, index) => {
        const isChecked = value === option.value;
        const optionDisabled = isDisabled || option.isDisabled;

        return (
          <Radio
            key={option.value}
            ref={(el) => {
              radioRefs.current[index] = el;
            }}
            name={name}
            value={option.value}
            label={option.label}
            checked={isChecked}
            onChange={(e) => {
              if (e.target.checked) {
                handleChange(option.value);
              }
            }}
            size={size}
            validationState={currentValidationState}
            isDisabled={optionDisabled}
            showErrorText={false}
            containerClassName={option.containerClassName}
            labelClassName={option.labelClassName}
          />
        );
      });
    };

    // Clone children and inject props
    const renderChildren = () => {
      if (!children) return null;

      return React.Children.map(children, (child, index) => {
        if (!React.isValidElement<RadioProps>(child)) {
          return child;
        }

        const childValue = child.props.value;
        const isChecked = value === childValue;
        const childDisabled = isDisabled || child.props.isDisabled;

        return React.cloneElement(child, {
          ...child.props,
          ref: (el: HTMLInputElement | null) => {
            radioRefs.current[index] = el;
          },
          name,
          checked: isChecked,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked) {
              handleChange(
                typeof childValue === "string"
                  ? childValue
                  : String(childValue || "")
              );
            }
            // Call original onChange if it exists
            if (child.props.onChange) {
              child.props.onChange(e);
            }
          },
          size: child.props.size || size,
          validationState:
            child.props.validationState || currentValidationState,
          isDisabled: childDisabled,
          showErrorText: false,
        } as Partial<RadioProps>);
      });
    };

    return (
      <div
        ref={ref}
        className={cn(
          "w-full flex flex-col",
          sizeConfig[size].gap,
          containerClassName
        )}
        {...props}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={infoHeading}
            infoDescription={infoDescription}
            LinkComponent={LinkComponent}
            linkText={linkText}
            linkHref={linkHref}
            onLinkClick={onLinkClick}
            htmlFor={id}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <div
          ref={groupRef}
          role="radiogroup"
          aria-label={label}
          aria-required={isRequired}
          aria-invalid={currentValidationState === "error"}
          aria-disabled={isDisabled}
          className={cn(
            "flex",
            orientation === "horizontal"
              ? "flex-row items-center"
              : "flex-col items-start",
            spacingConfig[spacing],
            groupClassName,
            className
          )}
          onKeyDown={handleKeyDown}
          id={id}
        >
          {options ? renderOptions() : renderChildren()}
        </div>

        <FormFooter
          helperText={displayHelperText}
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState === "error"
              ? "negative"
              : "default"
          }
          size={size}
          isDisabled={isDisabled}
          className="mt-1"
        />
      </div>
    );
  }
);

RadioGroup.displayName = "RadioGroup";

export { RadioGroup };
